import { useState } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocalServer } from '../../context/LocalServerContext';
import { useAuth } from '../../context/AuthContext';
import api, { downloadFile } from '../../services/api';

// Same roles as LocalServerUpdateButton — a PIN-authenticated cashier shouldn't
// see an installer download prompt.
const ALLOWED_ROLES = ['manager', 'partner', 'admin', 'super_admin'];

interface Build {
    version: string;
    os: 'win10' | 'win7';
    filename: string;
}

const OS_LABELS: Record<Build['os'], string> = {
    win10: 'Windows 10 / 11',
    win7: 'Windows 7',
};

/**
 * Manual "first install" entry point for the local-server installer — separate
 * from LocalServerUpdateButton, which only appears once an instance is already
 * connected and the cloud has a newer build. This one is for a restaurant that
 * hasn't deployed a local-server yet, so it stays visible until one is detected
 * (mirrors koolfood-relay's download grid on POSPrinters.tsx, same two-OS-variant
 * pattern — see hardware-gateway/gateway.service.ts).
 */
export default function LocalServerDownloadMenu() {
    const { isAvailable } = useLocalServer();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [builds, setBuilds] = useState<Build[] | null>(null);

    if (isAvailable || !user || !ALLOWED_ROLES.includes(user.role)) return null;

    const toggleOpen = async () => {
        const next = !open;
        setOpen(next);
        if (next && !builds) {
            try {
                const res = await api.get('/local-sync/builds');
                setBuilds(res.data.data.builds || []);
            } catch {
                toast.error('Erreur lors du chargement des installeurs disponibles.');
                setBuilds([]);
            }
        }
    };

    const handleDownload = async (build: Build) => {
        try {
            await downloadFile(`/local-sync/download-build/${build.filename}`, build.filename);
        } catch {
            toast.error('Échec du téléchargement.');
        }
        setOpen(false);
    };

    return (
        <div className="relative">
            <button
                onClick={toggleOpen}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                title="Télécharger le serveur local"
            >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden 2xl:inline">Serveur local</span>
                <ChevronDown className="h-3 w-3" />
            </button>

            {open && (
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {builds === null && (
                        <div className="px-3 py-2 text-xs text-gray-500">Chargement…</div>
                    )}
                    {builds?.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-500">Aucun installeur disponible.</div>
                    )}
                    {(['win10', 'win7'] as const).map((os) => {
                        const build = builds?.find((b) => b.os === os);
                        return (
                            <button
                                key={os}
                                onClick={() => build && handleDownload(build)}
                                disabled={!build}
                                className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left ${
                                    build ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {OS_LABELS[os]}
                                {build && <span className="text-gray-400">v{build.version}</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
